import { motion, AnimatePresence } from 'framer-motion';
import { useInquiry } from '@/hooks';
import type { Product } from '@/types';
import { cn } from '@/lib/utils';
import { CheckCircle, X, MessageCircle, ArrowRight } from 'lucide-react';

interface StickyMicroFormProps {
  product?: Product;
  className?: string;
  onClose?: () => void;
  isOpen: boolean;
  selectedColorName?: string;
}

/**
 * Sticky form that appears on scroll or button click
 * Allows users to quickly submit inquiry for a product
 */
export function StickyMicroForm({ product, className, onClose, isOpen, selectedColorName }: StickyMicroFormProps) {
  const { submitInquiry, isPending, isSuccess, error, resetForm } = useInquiry();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const userMessage = formData.get('message') as string;

    // Append color info to message if selected
    const finalMessage = selectedColorName
      ? `[Seçilen Renk: ${selectedColorName}]\n\n${userMessage}`
      : userMessage;

    submitInquiry({
      name: formData.get('name') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      message: finalMessage,
      productName: product?.title,
      productId: product?.id,
    } as any);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - Lighter for non-intrusive feel */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-[9998]"
          />

          {/* Modal Container - Bottom Right Floating Box */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'fixed bottom-4 right-4 md:bottom-8 md:right-8 z-[9999]',
              'w-[calc(100%-2rem)] md:w-[400px] max-h-[85vh] overflow-y-auto',
              'bg-white shadow-2xl rounded-xl',
              className
            )}
          >
            <div className="p-8">
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-charcoal transition-colors hover:bg-gray-50 rounded-full z-10"
              >
                <X size={20} />
              </button>

              {/* Header */}
              <div className="mb-6 text-center">
                <h3 className="font-display text-2xl text-charcoal mb-1">
                  {product ? 'Fiyat Teklifi Al' : 'Bize Ulaşın'}
                </h3>
                {product && (
                  <div className="text-sm text-gray-500 font-light px-4">
                    <p>
                      <span className="font-medium text-charcoal">{product.title}</span> için
                    </p>
                    {selectedColorName && (
                      <p className="mt-1 text-xs px-2 py-0.5 bg-gray-100 inline-block rounded text-charcoal">
                        Seçilen Renk: <span className="font-medium">{selectedColorName}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Form Content */}
              <AnimatePresence mode="wait">
                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-6"
                  >
                    <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-7 h-7 text-green-600" />
                    </div>
                    <h4 className="font-display text-lg text-charcoal mb-2">Talebiniz Alındı!</h4>
                    <p className="text-sm text-gray-500 mb-6">
                      En kısa sürede dönüş yapacağız.
                    </p>
                    <button
                      onClick={() => {
                        resetForm();
                        onClose?.();
                      }}
                      className="w-full py-3 bg-gray-100 text-charcoal text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors rounded-sm"
                    >
                      Kapat
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="space-y-4">
                      <input
                        type="text"
                        name="name"
                        placeholder="Adınız Soyadınız"
                        required
                        className="w-full px-0 py-2 border-b border-gray-200 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-sm bg-transparent"
                      />
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Telefon Numaranız"
                        required
                        className="w-full px-0 py-2 border-b border-gray-200 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-sm bg-transparent"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="E-posta Adresiniz"
                        required
                        className="w-full px-0 py-2 border-b border-gray-200 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-sm bg-transparent"
                      />
                      <textarea
                        name="message"
                        rows={2}
                        placeholder="Mesajınız..."
                        defaultValue={selectedColorName ? `Merhaba, ${product?.title} (${selectedColorName}) hakkında bilgi almak istiyorum.` : ''}
                        className="w-full px-0 py-2 border-b border-gray-200 focus:border-charcoal outline-none transition-all placeholder:text-gray-400 text-sm bg-transparent resize-none"
                      />
                    </div>

                    {error && (
                      <div className="p-3 bg-red-50 text-red-600 text-xs flex items-center justify-center gap-2 rounded-sm">
                        <span>⚠️</span> {error}
                      </div>
                    )}

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={isPending}
                        className={cn(
                          'w-full py-4 bg-charcoal text-white rounded-sm',
                          'text-xs font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-2',
                          'hover:bg-black transition-all shadow-lg hover:shadow-xl',
                          'disabled:opacity-70 disabled:cursor-not-allowed'
                        )}
                      >
                        {isPending ? 'Gönderiliyor...' : (
                          <>
                            Teklif İste <ArrowRight className="w-3 h-3" />
                          </>
                        )}
                      </button>

                      <a
                        href="https://wa.me/905537942161"
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center justify-center gap-2 text-xs text-gray-400 hover:text-green-600 transition-colors mt-4"
                      >
                        <MessageCircle size={14} />
                        WhatsApp ile iletişime geç
                      </a>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
